import { easeOutQuart, MIN_SNAP_DELTA_PX, SNAP_DURATION_MS } from './constants';

// Regras de snap e animacao do drawer.
// Aqui nao ha acoplamento com Vue nem com o DOM.

// Snap final de um gesto, MESMA regra para arrasto da alca e scroll da lista.
//
// Prioriza o SENTIDO do gesto (subir = abrir, descer = minimizar) e usa o quanto
// o usuario andou a partir do ponto inicial como compromisso: ao cruzar
// `progressRatio` do trecho ate o proximo snap naquela direcao, ja avanca para
// ele (e segue avancando enquanto cruzar o limiar do trecho seguinte). Assim um
// gesto curto e deliberado ja vai para o proximo nivel, sem depender de velocidade
// nem de "qual snap esta mais perto" (que faria gestos curtos voltarem ao inicio).
//
// - `startY`: posicao virtual onde o gesto comecou (ancora do sentido/distancia).
// - `y`: posicao virtual ao soltar.
// - Sem movimento liquido relevante: mantem o snap mais proximo do inicio.
export function resolveDirectionalSnap(input: {
	startY: number;
	y: number;
	snapsAscending: number[];
	progressRatio: number;
	restingPx: number;
}): number {
	const { startY, y, snapsAscending, progressRatio, restingPx } = input;

	const nearest = (pos: number): number =>
		snapsAscending.reduce(
			(best, s) => (Math.abs(s - pos) < Math.abs(best - pos) ? s : best),
			snapsAscending[0]!
		);

	const movedUp = y > startY + restingPx;
	const movedDown = y < startY - restingPx;
	if (!movedUp && !movedDown) return nearest(startY);

	// Snaps no sentido do gesto, do mais proximo ao mais distante do inicio.
	const directional = movedUp
		? snapsAscending.filter(s => s > startY)
		: [...snapsAscending].reverse().filter(s => s < startY);

	let target = nearest(startY);
	let from = startY;
	for (const snap of directional) {
		const threshold = from + progressRatio * (snap - from);
		const crossed = movedUp ? y >= threshold : y <= threshold;
		if (!crossed) break;
		target = snap;
		from = snap;
	}
	return target;
}

// Animador com RAF para transicao entre dois valores de scroll.
export interface SnapAnimator {
	isAnimating: () => boolean;
	animate(opts: {
		from: number;
		to: number;
		onTick: (value: number) => void;
		onDone?: () => void;
		customDuration?: number;
		customEasing?: (t: number) => number;
	}): void;
	cancel(): void;
}

export function createSnapAnimator(): SnapAnimator {
	let rafId: number | null = null;
	let animating = false;

	// Cancela animacao ativa (se houver).
	function cancel(): void {
		if (rafId !== null) {
			cancelAnimationFrame(rafId);
			rafId = null;
		}
		animating = false;
	}

	// Inicia animacao do valor `from` ate `to`.
	function animate(opts: {
		from: number;
		to: number;
		onTick: (value: number) => void;
		onDone?: () => void;
		customDuration?: number;
		customEasing?: (t: number) => number;
	}): void {
		cancel();
		const { from, to, onTick, onDone, customDuration, customEasing } = opts;
		const delta = to - from;
		if (Math.abs(delta) < MIN_SNAP_DELTA_PX) {
			onTick(to);
			onDone?.();
			return;
		}
		animating = true;
		const t0 = performance.now();
		const duration = customDuration ?? SNAP_DURATION_MS;
		const easing = customEasing ?? easeOutQuart;
		const tick = (now: number) => {
			const progress = Math.min((now - t0) / duration, 1);
			const eased = easing(progress);
			onTick(from + delta * eased);
			if (progress < 1) {
				rafId = requestAnimationFrame(tick);
			} else {
				rafId = null;
				animating = false;
				onDone?.();
			}
		};
		rafId = requestAnimationFrame(tick);
	}

	return {
		isAnimating: () => animating,
		animate,
		cancel
	};
}
